/**
 * Shared API payload types.
 *
 * **Every type here is derived from `schema.d.ts`**, which `npm run
 * generate:api-types` (or `make openapi`) regenerates from `docs/openapi.json`
 * — itself generated from the Django code by drf-spectacular and guarded by
 * `apps.api.tests.test_openapi_schema`. Nothing below re-describes a payload
 * by hand: a backend field change becomes a TypeScript error here, not a
 * runtime surprise in a screen.
 *
 * Two kinds of deviation from a raw `components['schemas'][…]` alias are
 * allowed, and each one is commented at its declaration site:
 *
 * 1. `Required<…>` where DRF marks a field optional because the *model* has a
 *    default, even though the response always carries it.
 * 2. Generic helpers OpenAPI cannot express at all (`Paginated<T>`).
 */
import type { components } from './schema';

/** Every named component of `docs/openapi.json`, as generated TypeScript. */
type Schemas = components['schemas'];

// ---------------------------------------------------------------------------
// Accounts (`docs/AUTH.md`).
// ---------------------------------------------------------------------------

/** A user's role, as returned by the API. */
export type UserRole = Schemas['UserRoleEnum'];

/**
 * The `me` payload, per `docs/AUTH.md`.
 *
 * `Required<…>`: `UserSerializer` leaves `first_name`, `phone` and friends
 * "not required" because the model gives them a blank default, but a
 * *response* always carries every one of them.
 */
export type User = Required<Schemas['User']>;

/** The subset of {@link User} fields `PATCH auth/me/` accepts, per `docs/AUTH.md`. */
export type ProfileUpdate = Partial<Pick<User, 'first_name' | 'last_name' | 'phone'>>;

/** Payload of `PATCH /api/v1/users/{id}/`: role, admin only. */
export type UserRoleUpdate = Partial<Pick<User, 'role'>>;

/**
 * Whether a {@link User}'s profile carries enough information to leave
 * `/onboarding`: a name.
 *
 * @param user - The signed-in user's profile.
 * @returns `true` once the required onboarding fields are filled in.
 */
export function isProfileComplete(user: User): boolean {
  return Boolean(user.first_name.trim() && user.last_name.trim());
}

// ---------------------------------------------------------------------------
// Generic envelopes.
// ---------------------------------------------------------------------------

/**
 * Generic DRF page envelope (`PAGE_SIZE = 50`, see `docs/CONVENTIONS.md`).
 * Declared by hand because OpenAPI 3.0.3 has no generics: the schema instead
 * carries one `Paginated<X>List` component per paginated endpoint, all with
 * this exact shape.
 */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Drop the `readonly` markers the generated schema puts on every server-owned
 * field. Response types keep them (a screen must not mutate a payload); the
 * MSW fixtures, which stand in for the *server*, use this to build and update
 * their in-memory rows.
 */
export type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** A `{code, detail}` error body, e.g. from a `4xx` conflict response. */
export interface ApiErrorBody {
  code?: string;
  detail: string;
}

// ---------------------------------------------------------------------------
// Asynchronous jobs (`GET /api/v1/tasks/{task_id}/`).
// ---------------------------------------------------------------------------

/** Celery state of an asynchronous job. */
export type TaskState = Schemas['StateEnum'];

/** Payload of `GET /api/v1/tasks/{task_id}/`. */
export type TaskStatus = Schemas['TaskStatus'];

/** States in which a task has stopped moving: stop polling on any of these. */
export const TERMINAL_TASK_STATES: readonly TaskState[] = ['SUCCESS', 'FAILURE'];

// ---------------------------------------------------------------------------
// Communes (`docs/API.md` §2).
// ---------------------------------------------------------------------------

/** One row of `GET /communes/`: the caller's commune, role and coverage. */
export type Commune = Required<Schemas['Commune']>;

/** Non-nominative door coverage counts of one commune. */
export type Coverage = Schemas['Coverage'];

/** One row of `GET /auth/me/`'s `memberships`, or of `POST /auth/charter/`. */
export type Membership = Required<Schemas['Membership']>;

/** A membership's role for one commune: `commune_admin` or `volunteer`. */
export type MembershipRole = Schemas['MembershipRoleEnum'];

/** Payload of `POST /auth/charter/`. */
export type CharterSignRequest = Schemas['CharterSignRequest'];

// ---------------------------------------------------------------------------
// Sessions, doors and the assignment state machine (`docs/API.md` §3-§6).
// ---------------------------------------------------------------------------

/** A canvassing session. */
export type Session = Required<Schemas['Session']>;

/** Payload of `POST /sessions/`. */
export type SessionCreateRequest = Schemas['SessionCreateRequest'];

/** `standard` (PAP) or `registration` (special inscription). */
export type SessionKind = Schemas['SessionKindEnum'];

/** `open` or `closed`. */
export type SessionState = Schemas['SessionStateEnum'];

/** Response of `POST /sessions/{id}/leave/`. */
export type LeaveResult = Schemas['LeaveResult'];

/** `GET /sessions/{id}/state/`: the light polling body. */
export type SessionStatePoll = Schemas['SessionState'];

/** Payload of `POST /sessions/{id}/position/`. */
export type PositionInputRequest = Schemas['PositionInputRequest'];

/**
 * `POST /sessions/{id}/close/`'s frozen wrap-up (`docs/DOMAIN.md` §4), and
 * `distance_m` from `?near=` (`docs/API.md` §3). Both are documented in
 * `docs/API.md` but not yet emitted by drf-spectacular: `closing_summary`
 * and `distance_m` are `SerializerMethodField`s populated only
 * conditionally (`state == "closed"`, or the request carrying `?near=`),
 * which drf-spectacular does not infer into the generated schema from a
 * `get_*` method. Hand-typed here, narrowly, as a third kind of deviation
 * from the raw schema (alongside the two `types.ts`'s own docstring already
 * names) — until the backend's `@extend_schema` override documents them.
 */
export interface SessionClosingSummary {
  doors_recorded: number;
  doors_released: number;
  participants: number;
  average_quality: number | null;
}

/** {@link Session}, with the two fields `docs/API.md` §3 promises but the schema doesn't yet type. */
export type SessionWithExtras = Session & {
  closing_summary?: SessionClosingSummary;
  distance_m?: number;
};

/** A door handed to a participant. */
export type Assignment = Required<Schemas['Assignment']>;

/** A door, as shown to the volunteer currently holding it. */
export type Door = Required<Schemas['Door']>;

/** One elector of a door's household. */
export type HouseholdMember = Schemas['HouseholdMember'];

/** `18-24`, `25-34`, `35-49`, `50-64`, `65+`, or `null` when unknown. */
export type AgeBand = Schemas['AgeBandEnum'];

/** `voted`, `abstained` or `unknown`. */
export type TurnoutStatus = Schemas['TurnoutStatusEnum'];

/** `real`, `phantom_h1` or `phantom_h2`. */
export type DoorKind = Schemas['DoorKindEnum'];

/** `unseen`, `assigned` or `recorded`. */
export type DoorState = Schemas['DoorStateEnum'];

/** `GET /sessions/{id}/building/`. */
export type Building = Required<Schemas['Building']>;

/** One door of the building view — counts only, never a name. */
export type BuildingDoor = Schemas['BuildingDoor'];

// ---------------------------------------------------------------------------
// Visits (`docs/API.md` §6).
// ---------------------------------------------------------------------------

/** Payload of `POST /assignments/{id}/visit/`. */
export type VisitInputRequest = Schemas['VisitInputRequest'];

/** `absent`, `exchange`, `refusal`, `come_back`, `registration_info` or `moved`. */
export type VisitOutcome = Schemas['VisitOutcomeEnum'];

/** Response of `POST /assignments/{id}/visit/`. */
export type VisitRecord = Required<Schemas['VisitRecord']>;

/** Row of `GET /visits/`. */
export type Visit = Required<Schemas['Visit']>;

// ---------------------------------------------------------------------------
// Registration cases and follow-ups (`docs/API.md` §7).
// ---------------------------------------------------------------------------

/** A registration case, with its follow-up messages. */
export type RegistrationCase = Required<Schemas['RegistrationCase']>;

/** Payload of `POST /doors/{id}/registration-case/`. */
export type RegistrationCaseInputRequest = Schemas['RegistrationCaseInputRequest'];

/** Payload of `POST /registration-cases/{id}/advance/`. */
export type RegistrationCaseAdvanceRequest = Schemas['RegistrationCaseAdvanceRequest'];

/** `info_given`, `filed`, `confirmed` or `closed`. */
export type CaseStage = Schemas['CaseStageEnum'];

/** One row of a case's follow-up sequence. */
export type FollowupMessage = Required<Schemas['FollowupMessage']>;

/** `j2`, `j7` or `j14`. */
export type FollowupStep = Schemas['StepEnum'];

/** `scheduled`, `sent`, `opened`, `replied`, `unsubscribed`, `failed` or `cancelled`. */
export type FollowupStatus = Schemas['FollowupStatusEnum'];

// ---------------------------------------------------------------------------
// Engagements (`docs/API.md` §8).
// ---------------------------------------------------------------------------

/** Representation of an engagement: no elector or door ever appears here, by design. */
export type Engagement = Required<Schemas['Engagement']>;

/** Payload of `POST /engagements/`. */
export type EngagementInputRequest = Schemas['EngagementInputRequest'];

/** `contact`, `newsletter`, `support_campaign`, `building_referent` or `join_group`. */
export type EngagementKind = Schemas['EngagementKindEnum'];

/** How many engagements of a commune fall under each kind. */
export type EngagementCounts = Schemas['EngagementCounts'];

// ---------------------------------------------------------------------------
// Commune admin (`docs/API.md` §9) and national admin (`docs/API.md` §10).
// ---------------------------------------------------------------------------

/** `GET /communes/{insee}/dashboard/`. */
export type CommuneDashboard = Schemas['CommuneDashboard'];

/** Row of `GET /communes/{insee}/notes/` and the purge/keep responses. */
export type Note = Required<Schemas['Note']>;

/** `GET /communes/{insee}/members/` row. */
export type CommuneMember = Required<Schemas['CommuneMember']>;

/** One row of `GET /national/overview/`: counts only, never a name. */
export type NationalCommuneOverview = Schemas['NationalCommuneOverview'];
